import axios from "axios";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import {
  LoaderFunction,
  redirect,
  useLoaderData,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import { Models } from "@/api/types";
import useHistory from "@/hooks/useHistory";
import TopNav from "@/molecules/navbars/TopNav";
import { queryClient } from "@/pages/Root";
import { get, getApiBaseUrl, ppost } from "api";
import { Button } from "atoms/button/Button";
import ChevronLeft from "atoms/icon/icons/chevron_left.svg";

import BookingCalendar from "./BookingCalendar";
import BookingProvidersList from "./BookingProvidersList";

const load = async (id: string) => {
  const platformLocation = await queryClient.fetchQuery(
    ["/platform-location"],
    () => get("/platform-location"),
    { staleTime: 1000 * 60 * 10 }
  );

  const listing = await queryClient.fetchQuery(
    ["/listings/{id}", { id }],
    () => get("/listings/{id}", { id }),
    { staleTime: 1000 * 60 * 10 }
  );

  const therapists = await queryClient.fetchQuery(
    [
      "/partner-emr/therapists",
      platformLocation.zenotiId,
      listing.partnerEmrId,
    ],
    () => {
      if (!platformLocation.zenotiId || !listing.partnerEmrId) {
        throw new Error("Missing required platform location or service ID");
      }
      return get("/partner-emr/therapists", {
        ExternalPlatformLocationId: platformLocation.zenotiId,
        ServiceId: listing.partnerEmrId,
      });
    },
    { staleTime: 1000 * 60 * 10 }
  );

  return { therapists, listing, platformLocation };
};

type LoaderData = Awaited<ReturnType<typeof load>>;
export const bookingProvidersLoader: LoaderFunction = ({ params: { id } }) => {
  if (!id) return redirect("/");

  return load(id);
};

const mapTherapistsToProviders = (
  therapists: unknown
): Partial<Models<"DisplayProvider">>[] => {
  // Handle case where response might be wrapped in result or direct
  const data =
    typeof therapists === "object" &&
    therapists !== null &&
    "result" in therapists &&
    therapists.result !== null
      ? therapists.result
      : therapists;

  // Type guard to check if data has Therapists array
  if (
    typeof data === "object" &&
    data !== null &&
    "Therapists" in data &&
    Array.isArray(data.Therapists)
  ) {
    return data.Therapists.map(
      (therapist: unknown): Partial<Models<"DisplayProvider">> => {
        if (
          typeof therapist === "object" &&
          therapist !== null &&
          "Id" in therapist &&
          "Name" in therapist &&
          typeof therapist.Id === "string" &&
          typeof therapist.Name === "string"
        ) {
          return {
            id: therapist.Id,
            displayName: therapist.Name,
          };
        }
        return {};
      }
    ).filter((provider) => provider.id !== undefined);
  }

  return [];
};

const BookingProviders = () => {
  const navigate = useNavigate();
  // Get the packageId and priceId from the search params
  const [searchParams] = useSearchParams();
  const packageId = searchParams.get("packageId");
  const priceId = searchParams.get("priceId");
  const membershipId = searchParams.get("membershipId");

  const { routeBackNumber } = useHistory();
  const { therapists, listing, platformLocation } =
    useLoaderData() as LoaderData;

  const providers = mapTherapistsToProviders(therapists);

  const [selectedProviderId, setSelectedProviderId] = useState<
    string | null | undefined
  >(undefined);
  const [showCalendar, setShowCalendar] = useState(false);
  const [isLoadingSchedules, setIsLoadingSchedules] = useState(false);
  const [isLoadingTimeSlots, setIsLoadingTimeSlots] = useState(false);
  const [selectedDate, setSelectedDateState] = useState<Date | undefined>(
    undefined
  );
  // Track the latest date request to prevent race conditions
  const latestDateRequestRef = useRef<number | null>(null);

  const setSelectedDate = async (date: Date | undefined) => {
    setSelectedDateState(date);

    if (!date) {
      latestDateRequestRef.current = null;
      return;
    }

    // Create a unique identifier for this request using timestamp
    const requestId = Date.now();
    latestDateRequestRef.current = requestId;

    setIsLoadingTimeSlots(true);
    try {
      const responses = await fetchProviderSchedules(
        date,
        false,
        selectedProviderId
      );

      // Check if this is still the latest request before updating state
      if (latestDateRequestRef.current !== requestId) {
        // This request is stale, ignore the response
        return;
      }

      if (!responses) {
        throw new Error("Failed to fetch provider schedules");
      }

      // Responses are already parsed as objects
      const schedulesFromResponse = responses.map((data) => {
        const openSlots = data.OpenSlots || [];
        const selectedService = data.SlotBookings[0].Services.find(
          (service: { Service: { Id: string } }) =>
            service.Service.Id === listing.partnerEmrId
        );
        return openSlots.map(
          (day: {
            Time: string;
            Available: boolean;
            HolidayType: string | null;
          }) => ({
            date: dayjs(day.Time).format("YYYY-MM-DD"),
            startTime: day.Time,
            endTime: dayjs(day.Time)
              .add(selectedService.Service.Duration, "minutes")
              .format("YYYY-MM-DDTHH:mm:ss"),
            isAvailable: day.Available,
          })
        );
      });

      // Double-check the request is still current before updating
      if (latestDateRequestRef.current !== requestId) {
        return;
      }

      const updatedSchedules = providerSchedules.filter(
        (schedule) => schedule.date !== dayjs(date).format("YYYY-MM-DD")
      );

      setProviderSchedules([...schedulesFromResponse[0], ...updatedSchedules]);
      scrollToBottom();
    } catch (error) {
      // Only log errors for the latest request
      if (latestDateRequestRef.current === requestId) {
        console.error("Error fetching time slots:", error);
      }
    } finally {
      // Only update loading state if this is still the latest request
      if (latestDateRequestRef.current === requestId) {
        setIsLoadingTimeSlots(false);
      }
    }
  };
  const [selectedTime, setSelectedTime] = useState<string | undefined>(
    undefined
  );
  const [providerSchedules, setProviderSchedules] = useState<
    Models<"DisplayProviderAvailabilitySlot">[]
  >([]);
  const [isBooking, setIsBooking] = useState(false);
  const [bookingError, setBookingError] = useState<string | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    setTimeout(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTo({
          top: scrollContainerRef.current.scrollHeight,
          behavior: "smooth",
        });
      }
    }, 100);
  };

  // Use useEffect to handle navigation instead of returning redirect
  useEffect(() => {
    if (!packageId || !priceId) {
      navigate("..");
    }
  }, [packageId, priceId, navigate]);

  // Early return for loading state or invalid params
  if (!packageId || !priceId) {
    return null; // or a loading spinner
  }

  const fetchProviderSchedules = async (
    startDate: Date,
    includeFutureDays = true,
    providerIdOverride?: string | null
  ) => {
    try {
      // Create dates for 4 requests: today, +7 days, +14 days, +21 days
      const dates = includeFutureDays
        ? [0, 7, 14, 21, 28].map((days) =>
            dayjs(startDate).add(days, "day").format("YYYY-MM-DD HH:mm:ss")
          )
        : [dayjs(startDate).format("YYYY-MM-DD HH:mm:ss")];

      // Validate required fields
      if (!platformLocation.zenotiId || !listing.partnerEmrId) {
        throw new Error("Missing required platform location or service ID");
      }

      const externalPlatformLocationId = platformLocation.zenotiId;
      const serviceId = listing.partnerEmrId;

      const actualProviderId = providerIdOverride ?? selectedProviderId;

      // Create array of fetch promises for parallel execution
      // Use axios directly with responseType: 'text' to force string response
      // which the post function can handle
      const fetchPromises = dates.map((centerDate) => {
        const payload = {
          externalPlatformLocationId,
          serviceId,
          requestedAppointmentDate: dayjs(centerDate).format("YYYY-MM-DD"),
          ...(actualProviderId && {
            selectedWellnessProviderExternalId: actualProviderId,
          }),
        };

        return axios
          .post(
            `${getApiBaseUrl()}/api/v1/partner-emr/appointments/available-times`,
            payload,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: axios.defaults.headers.common[
                  "Authorization"
                ] as string,
              },
              responseType: "text", // Force axios to return string, not parsed JSON
            }
          )
          .then((response) => response.data); // Extract the string data
      });

      // Execute all requests in parallel
      const responses = await Promise.all(fetchPromises);

      // Parse string responses to objects
      return responses.map((response) => {
        if (typeof response === "string") {
          return JSON.parse(response);
        }
        return response;
      });
    } catch (error) {
      console.error("Error fetching provider schedules:", error);
    }
  };

  const handleProviderSelection = async (providerId: string | null) => {
    setSelectedProviderId(providerId);
    setIsLoadingSchedules(true);

    try {
      const responses = await fetchProviderSchedules(
        new Date(),
        true,
        providerId
      );
      if (!responses) {
        throw new Error("Failed to fetch provider schedules");
      }

      // Extract FutureDays from responses (already parsed as objects)
      const schedulesFromResponse = responses.map((data) => {
        const futureDays = data.FutureDays || [];

        // Transform each day: rename Day to date, remove HolidayType, rename IsAvailable to isAvailable
        return futureDays
          .filter((day: { IsAvailable: boolean }) => day.IsAvailable)
          .map(
            (day: {
              Day: string;
              IsAvailable: boolean;
              HolidayType: string | null;
            }) => ({
              date: dayjs(day.Day).format("YYYY-MM-DD"),
              isAvailable: day.IsAvailable,
            })
          );
      });

      // Combine all schedules from all responses
      const allSchedules = schedulesFromResponse.flat();

      setProviderSchedules(allSchedules);

      // Only show calendar after getting the result
      setShowCalendar(true);
    } catch (error) {
      console.error("Error fetching provider schedules:", error);
      setProviderSchedules([]);
      // Still show calendar even if there's an error
      setShowCalendar(true);
    } finally {
      setIsLoadingSchedules(false);
    }
  };

  const handleBackToProviders = () => {
    setShowCalendar(false);
    setSelectedDateState(undefined);
    setSelectedTime(undefined);
    setBookingError(null);
  };

  const getSelectedProvider = () => {
    if (!selectedProviderId || !Array.isArray(providers)) return null;
    return (
      providers.find((provider) => provider.id === selectedProviderId) || null
    );
  };

  const handleBookingError = (errorMessage: string) => {
    console.error("Booking request failed: ", errorMessage);
    setBookingError(errorMessage);
    setIsBooking(false);
    setSelectedTime(undefined);
    scrollToBottom();
  };

  const handleBook = async () => {
    if (!selectedDate || !selectedTime || !listing) return;

    setBookingError(null);
    setIsBooking(true);

    try {
      // Create the booking request
      const [hours, minutes] = selectedTime.split(":").map(Number);
      const selectedDateTime = new Date(selectedDate);
      selectedDateTime.setHours(hours, minutes, 0, 0);
      const bookingRequest: Models<"CreateBookingRequest"> = {
        platformLocationId: platformLocation.id,
        items: [
          {
            packageId,
            startTimeUtc: `${dayjs(selectedDateTime).format(
              "YYYY-MM-DDTHH:mm:ss"
            )}Z`,
            ...(selectedProviderId && {
              externalProviderId: selectedProviderId.toString(),
            }),
          },
        ],
      };

      // Make the booking request
      let bookingResponse;
      try {
        bookingResponse = await ppost("/platform-location/{locationId}/book")({
          locationId: platformLocation.id,
          ...bookingRequest,
        });
      } catch (error) {
        handleBookingError(
          "Booking failed. Please try again or contact support."
        );
        return;
      }

      if (!bookingResponse?.isReserved) {
        handleBookingError(
          bookingResponse?.errorMessage || "Booking failed. Please try again."
        );
        return;
      }

      const appointmentId = bookingResponse?.appointmentId;

      await ppost("/orders/current/lineitems")({
        packageListingId: listing.id,
        packageId: packageId,
        packagePriceId: priceId,
        ...(membershipId && { membershipId }),
        ...(appointmentId && { appointmentId }),
      });

      queryClient.invalidateQueries({
        queryKey: ["/orders/current/summary"],
      });

      navigate("/cart");
    } catch (error) {
      console.error("Booking failed:", error);
      setIsBooking(false);
    }
  };

  return (
    <div className="flex h-screen flex-col">
      <TopNav
        startIconSVG={ChevronLeft}
        onStartIconClick={() => navigate(routeBackNumber)}
      >
        <div className="flex max-h-[32px] items-center">
          <h1 className="text-xl">
            {listing.type == "Treatment" ? "Treatments" : "Packages"}
          </h1>
        </div>
      </TopNav>
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto">
        <div className="flex flex-col items-center pt-6">
          {/* Treatment Name */}
          <div className="break-words text-center font-serif text-h1">
            {listing.title}
          </div>

          <div className="w-full">
            {isLoadingSchedules ? (
              <div className="mt-8 flex justify-center">
                <div className="flex items-center space-x-2">
                  <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-brand-color"></div>
                  <span className="text-sm text-gray-600">
                    Loading schedules...
                  </span>
                </div>
              </div>
            ) : showCalendar ? (
              <BookingCalendar
                providerName={getSelectedProvider()?.displayName || null}
                onBackToProviders={handleBackToProviders}
                providerSchedules={providerSchedules}
                selectedDate={selectedDate}
                selectedTime={selectedTime}
                setSelectedDate={setSelectedDate}
                setSelectedTime={setSelectedTime}
                isLoadingTimeSlots={isLoadingTimeSlots}
              />
            ) : (
              <BookingProvidersList
                providers={providers}
                selectedProviderId={selectedProviderId}
                onProviderSelection={handleProviderSelection}
              />
            )}
          </div>
          {bookingError && (
            <div className="bg-red-50 border-red-200 rounded-md border-b border-t p-3">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg
                    className="text-red-500 h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-red-500 text-sm">{bookingError}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Book Button */}
      <div className="sticky bottom-[calc(59px_+_env(safe-area-inset-bottom)-1px)] w-full bg-white p-4">
        <Button
          fullWidth
          disabled={!selectedTime || isBooking}
          onClick={handleBook}
        >
          {isBooking ? (
            <div className="flex items-center justify-center space-x-2">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
              <span>Booking...</span>
            </div>
          ) : (
            "Book"
          )}
        </Button>
      </div>
    </div>
  );
};

export default BookingProviders;
